import z from 'zod';
import { Threshold } from '@modules/iot/model';
import { ReactComponent as HorteeSensorIcon } from '@assets/icons/hortee-sensor.svg';
import { ReactComponent as SensoterraSensorIcon } from '@assets/icons/sensoterra-sensor.svg';
import { ReactComponent as SinafisSensorIcon } from '@assets/icons/sinafis-sensor.svg';
import { FC } from 'react';
import { StringifiableRecord } from 'query-string';
import { Zone } from '@modules/iot/zones/model';
import { Measure } from '@modules/measures/model';
import { Utils } from '@shared/utils/model';
import { LocalDateTime } from '@shared/modules/dates';
import { Sensoterra } from '@modules/sensors/sensoterra/model';
import { Sinafis } from '@modules/sensors/sinafis/model';
import { Hortee } from '@modules/sensors/hortee/model';
import sinafisLogo from '@assets/logos/sinafis.svg';
import horteeLogo from '@assets/logos/hortee.svg';
import sensoterraLogo from '@assets/logos/sensoterra.svg';
import { MantineColor } from '@mantine/core';
import * as Eq from 'fp-ts/Eq';
import * as S from 'fp-ts/string';
import { Geo } from '@shared/modules/geo/model';
import { SensorFilter } from '@modules/sensors/map/filters/model';

export namespace Sensor {
  export const Id = z.string().uuid().brand('SensorId');
  export type Id = z.infer<typeof Id>;
  export const idEq: Eq.Eq<Id> = S.Eq;
  export const Serial = z.string().uuid().brand('SensorSerial');
  export type Serial = z.infer<typeof Serial>;

  export enum Type {
    Sensoterra = 'sensoterra',
    Hortee = 'hortee',
    Sinafis = 'sinafis',
  }

  export const typeIcon: Record<Type, FC<React.ComponentProps<'svg'> & { title?: string }>> = {
    [Type.Sensoterra]: SensoterraSensorIcon,
    [Type.Hortee]: HorteeSensorIcon,
    [Type.Sinafis]: SinafisSensorIcon,
  };

  export const typeTitle: Record<Type, string> = {
    [Type.Sensoterra]: 'Sensoterra',
    [Type.Hortee]: 'Hortee',
    [Type.Sinafis]: 'Sinafis',
  };

  export const typeLogo: Record<Type, string> = {
    [Type.Sensoterra]: sensoterraLogo,
    [Type.Hortee]: horteeLogo,
    [Type.Sinafis]: sinafisLogo,
  };

  export const typeEq: Eq.Eq<Type> = S.Eq;

  export namespace Probe {
    export const Identifier = {
      ...Sensoterra.Probe.Identifier,
      ...Hortee.Probe.Identifier,
      ...Sinafis.Probe.Identifier,
    };

    export type Identifier = (typeof Identifier)[keyof typeof Identifier];

    export const identifierLabel: Record<Identifier, string> = {
      [Identifier.Ground]: 'Sol',
      [Identifier.Ground1]: 'Sol 1',
      [Identifier.Ground2]: 'Sol 2',
      [Identifier.Transmitter]: 'Transmetteur',
      [Identifier.Leaf]: 'Foliaire',
    };

    export const identifierColor: Record<Identifier, MantineColor> = {
      [Identifier.Ground]: '#afafaf',
      [Identifier.Ground1]: '#afafaf',
      [Identifier.Ground2]: '#afafaf',
      [Identifier.Transmitter]: '#333333',
      [Identifier.Leaf]: '#ffffff',
    };
  }

  export type Probe<Type extends Sensor.Type> = {
    [Sensor.Type.Sensoterra]: Sensoterra.Probe.Identifier;
    [Sensor.Type.Hortee]: Hortee.Probe.Identifier;
    [Sensor.Type.Sinafis]: Sinafis.Probe.Identifier;
  }[Type];
}

export interface Sensor<Type extends Sensor.Type = Sensor.Type> {
  id: Sensor.Id;
  type: Type;
  name: string;
}

export namespace ActiveSensor {
  import INSEECode = Geo.City.INSEECode;
  import PostalCode = Utils.PostalCode;

  export interface Filter extends StringifiableRecord {
    search: string | null;
    zoneId: Zone.Id | null;
    sensorType: Sensor.Type | null;
    sinafisProbeIdentifier: Sinafis.Probe.Identifier | null;
    alertLevel: Threshold.Level | null;
    alertType: Measure.Type | null;
    code: INSEECode | null;
    postalCode: PostalCode | null;
    filterId: SensorFilter.Id | null;
  }

  export interface FilterWithCity extends Filter {
    city: string | null;
  }

  export type Config<Type extends Sensor.Type> = {
    [Sensor.Type.Sensoterra]: Sensoterra.Config;
    [Sensor.Type.Hortee]: Hortee.Config;
    [Sensor.Type.Sinafis]: Sinafis.Config;
  }[Type];

  export interface Alert<Type extends Sensor.Type> {
    probe: Sensor.Probe<Type>;
    type: Measure.Type;
    level: Threshold.Level;
    since: LocalDateTime;
  }

  export const defaultFilter: Filter = {
    search: null,
    zoneId: null,
    sensorType: null,
    sinafisProbeIdentifier: null,
    alertLevel: null,
    alertType: null,
    postalCode: null,
    code: null,
    filterId: null,
  };

  export interface List {
    zones: Array<Zone>;
    sensors: Array<ActiveSensor>;
  }

  export interface Impl<Type extends Sensor.Type> extends Sensor<Type> {
    serial: Sensor.Serial;
    zone: Zone | null;
    location: Utils.GPSCoordinates;
    config: ActiveSensor.Config<Type>;
    measures: Measure.Last.Impl<Type> | null;
    alerts: Array<ActiveSensor.Alert<Type>>;
    comment: string | null;
  }
}

export type ActiveSensor =
  | ActiveSensor.Impl<Sensor.Type.Hortee>
  | ActiveSensor.Impl<Sensor.Type.Sinafis>
  | ActiveSensor.Impl<Sensor.Type.Sensoterra>;
